// @ts-nocheck
import axios from 'axios'
import type { AxiosRequestConfig } from 'axios'
import type { ResponseData, IRequestConfig } from '@/types/index'
import i18n from '@/i18n'
import blueimp from 'blueimp-md5'
import { useUserInfo, useSystemInfo } from '@/store'

import {
  isIOS,
  isAndroid,
  isFromIOSScreen,
  checkIfInPWA,
  isInAndroidApp,
  isInIosApp,
  isWindows,
  isMacOS,
  isApl,
} from '@/env'

interface PoolItem {
  promise: Promise<ResponseData>
  timestamp: number
  completed?: boolean
}

const merchantConfig = computed(() => useSystemInfo().merchantConfig)

const client = axios.create({
  baseURL: '/api/',
  timeout: 12000,
})

client.interceptors.request.use(
  (config) => {
    config.headers['cache-control'] = 'no-cache'
    // console.log("发起request-->config.headers", config.headers)
    const token = useUserInfo().userInfo?.token
    // token && (config.headers['Access-Token'] = token);
    token && (config.headers['Authorization'] = token)
    const clientIp = localStorage.getItem('clientIp') || ''
    clientIp && (config.headers['Client-Ip'] = clientIp)

    // 增加上报类型
    addTrackPrams(config.headers)
    return config
  },
  (err) => err,
)

client.interceptors.response.use(
  (res) => {
    const { t } = i18n.global

    if (res.data.code != 1) {
      if (res.data.code == -28 || res.data.code == -29) {
        console.warn(res.data.code, res.data)

        useUserInfo().logout()
        if (merchantConfig.value) {
          merchantConfig.value.siteStatus = res.data.code == -28 ? 'maintain' : 'freeze'
        } else if (location.pathname !== '/maintain')
          location.href = '/maintain?s=1'
        return res.data
      }

      if (res.data.code == -5003) {
        //活动已结束返回首页
        location.href = location.origin +  '/home'
        return
      }
      const dontNeedToast = [-5018] //不需要弹框提示的错误码集
      if (!res.config.skipToast && !dontNeedToast.includes(res.data.code)) {
        const hasKey = i18n.global.te(res.data.i18n)
        if (hasKey) {
          showToast({
            message: t(res.data.i18n),
            duration: 3000,
            zIndex: 3000,
            className: '!z-[3000]'
          })
        } else {
          console.warn(res.data.i18n, 'not found, msg:', t(res.data.i18n))
        }
      } else {
        closeToast()
      }
    }

    return res.data
  },
  (err) => {
    console.error('error', err)

    if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
      return Promise.reject('timeout')
    }

    if (err.code === 'ERR_CANCELED') {
      return Promise.reject('canceled')
    }

    if (err.status == 401 ) {
      // unauthorized
      useUserInfo().logout()
      // location.href = '/login'
      return Promise.reject('unauthorized')
    }

    if (err.status == 503) {
      if (merchantConfig.value) merchantConfig.value.siteStatus = 'maintain'
      else if (location.pathname !== '/maintain')
        location.href = '/maintain?s=1'

      console.warn('503', err)
      return
    }

    return err
  },
)

const addTrackPrams = (headers: any) => {
  let loginUserType = 'unknown'
  if (isIOS) loginUserType = 'iOS_H5'
  if (isAndroid) loginUserType = 'Android_H5'
  if (!isIOS && !isAndroid) loginUserType = 'PC'
  if (checkIfInPWA()) loginUserType = 'PWA'
  if (isInAndroidApp) loginUserType = 'Android_APP'
  if (isInAndroidApp && isApl) loginUserType = 'Android_H5'
  if (isInIosApp) loginUserType = 'iOS_APP'
  if (isInIosApp && isApl) loginUserType = 'iOS_H5'
  if (isFromIOSScreen) loginUserType = 'iOS_Bookmark'

  let clientSystemType = 'unknown'
  if (isIOS) clientSystemType = 'iOS'
  if (isAndroid) clientSystemType = 'Android'
  if (isWindows) clientSystemType = 'Windows'
  if (isMacOS) clientSystemType = 'MacOS'

  const params = {
    // 终端类型 苹果H5、安卓H5、PC、PWA、APK、APK上架包、iOS、iOS书签
    loginUserType,
    // 系统类型：Android、iOS、Windows等
    clientSystemType,
    // equipmentIdentification 设备标识：获取设备的唯一标识符，例如：a3dc575268c1e7f4d9c0e4aad7c7769f
    equipmentIdentification:
      localStorage.getItem('yz_finger_print_key') || 'unknown',
    // equipmentName 设备名称：手机新型号或代号，比如 iPhone 16、VIVO、Honor 15等
    equipmentName: localStorage.getItem('deviceName') || 'unknown',
  }

  Object.entries(params).forEach(([k, v]) => {
    headers[k] = v
  })
}

const requestPool = new Map<string, PoolItem>()
const TIMEOUT_DURATION = 1000 // 1秒内的重复请求会被过滤

const getFormDataString = (formData: FormData): string => {
  const pairs: string[] = []
  formData.forEach((value, key) => {
    if (value instanceof File) {
      pairs.push(`${key}:${value.name}-${value.size}`)
    } else {
      pairs.push(`${key}:${value}`)
    }
  })
  return pairs.sort().join('&') // 排序以确保顺序一致
}

const getParamsString = (params: any): string => {
  return Object.entries(params)
    .map((item) => item.join('='))
    .sort()
    .join('&')
}

const request = async (
  config: AxiosRequestConfig & {
    skipToast?: boolean
  },
  {
    skipCheck = false,
    skipToast = false,
    skipTime = TIMEOUT_DURATION,
    signal = null,
  }: IRequestConfig = {},
): Promise<ResponseData> => {
  if (skipCheck) return client.request(config)

  config.skipToast = skipToast
  config.signal = signal

  // 处理请求数据，特别是 FormData
  let dataString = ''
  if (config.data) {
    if (config.data instanceof FormData) {
      dataString = getFormDataString(config.data)
    } else {
      dataString = JSON.stringify(config.data)
    }
  }

  let paramsString = ''
  if (config.params) {
    paramsString = getParamsString(config.params)
  }

  const requestToken = blueimp(
    `${config.method}-${config.url}-${paramsString}-${dataString}`,
  )

  const now = Date.now()
  const existingRequest = requestPool.get(requestToken)

  // console.log('request', existingRequest, `${config.method}-${config.url}${paramsString ? '-' + paramsString : ''}${dataString ? '-' + dataString : ''}`);
  if (existingRequest && now - existingRequest.timestamp < skipTime) {
    console.warn(
      `repeated request in ${skipTime}ms`,
      `\n${config.method}-${config.url}${paramsString ? '-' + paramsString : ''}${dataString ? '-' + dataString : ''}`,
    )
    return existingRequest.promise
  }

  const req = client.request(config).finally(() => {
    const item = requestPool.get(requestToken)
    if (item) {
      item.completed = true
      // 1秒后删除请求记录
      setTimeout(() => {
        requestPool.delete(requestToken)
      }, skipTime)
    }
  })

  requestPool.set(requestToken, {
    promise: req,
    timestamp: now,
  })

  return req
}

// export { ResponseData }
export default request

export { addTrackPrams }
